<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-7xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="sci-page-header pb-4">
        <h1 class="sci-page-title-only">Proprietários</h1>
        <p class="text-sm text-slate-500">Gerenciamento de proprietários da frota municipal</p>
      </div>

      <!-- LISTAGEM -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <!-- Toolbar do Grid -->
          <div class="px-4 py-4 flex items-center w-full" style="background-color: #333333;">
              <div class="flex items-center gap-2 flex-none">
                <v-icon color="orange" small>mdi-account-group</v-icon>
                <h2 class="text-white font-bold uppercase tracking-wider text-[11px]">PROPRIETÁRIOS CADASTRADOS</h2>
              </div>
              
              <div class="flex items-center gap-2 flex-1 justify-end">
                  <div class="flex-none">
                <button 
                  v-if="isAdmin || isPrefecture"
                  @click="$router.push('/proprietario/create')" 
                  class="bg-orange-500 hover:bg-orange-600 text-white shadow-lg flex items-center justify-center gap-2 rounded-lg px-6 py-2 transition-all w-fit max-max whitespace-nowrap"
                  style="width: auto !important; flex: none !important;"
                >
                  <v-icon color="white" small>mdi-plus</v-icon>
                  <span class="font-bold text-sm">NOVO</span>
                </button>
                  </div>

                  <button @click="showSearch = !showSearch" class="p-2 rounded-lg text-slate-400 hover:bg-slate-600 hover:text-white transition-colors">
                     <v-icon color="currentColor">mdi-magnify</v-icon>
                  </button>
              </div>
          </div>

          <!-- Inline Search -->
           <v-expand-transition>
            <div v-if="showSearch" class="bg-slate-50 px-6 py-4 border-b border-slate-200 shadow-inner">
              <div class="flex items-center w-full px-3 py-2 bg-white border border-slate-300 rounded-lg shadow-sm focus-within:ring-1 focus-within:ring-orange-500 focus-within:border-orange-500 transition-all">
                  <v-icon color="#94a3b8" small class="mr-2">mdi-magnify</v-icon>
                  <input 
                    v-model="search" 
                    type="text" 
                    placeholder="Pesquisar por nome, CPF/CNPJ..." 
                    class="w-full text-sm text-slate-700 placeholder-slate-400 focus:outline-none bg-transparent border-none p-0"
                  >
              </div>
            </div>
           </v-expand-transition>

          <!-- Tabela -->
          <div class="overflow-x-auto">
             <v-data-table
              :headers="headers"
              :items="data"
              :search="search"
              :loading="loader"
              :items-per-page="10"
              class="custom-table"
              hide-default-footer
              @page-count="pageCount = $event"
              :page.sync="page"
            >
              <template v-slot:item.name="{ item }">
                <span class="text-sm font-bold text-slate-700 uppercase tracking-tight">{{ item.name }}</span>
              </template>

              <template v-slot:item.document="{ item }">
                <span class="text-xs font-mono text-slate-400">{{ item.document || '---' }}</span>
              </template>

              <template v-slot:item.action="{ item }">
                 <div class="flex justify-end gap-2">
                    <button 
                      @click="$router.push(`/proprietario/edit/${item.id}`)"
                      class="p-2 rounded-lg text-slate-400 hover:bg-orange-50 hover:text-orange-500 transition-colors"
                    >
                      <v-icon small color="currentColor">mdi-pencil</v-icon>
                    </button>
                    <button 
                      @click="deleteItem(item.id)"
                      class="p-2 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                    >
                      <v-icon small color="currentColor">mdi-delete</v-icon>
                    </button>
                 </div>
              </template>
             </v-data-table>

             <!-- Custom Pagination Footer -->
             <div class="bg-slate-50 px-6 py-4 border-t border-slate-200 flex flex-column md:flex-row justify-between items-center gap-4 text-xs text-slate-500 font-medium w-full">
                 <span>Exibindo {{ paginationStart }} de {{ data.length }} registros</span>
                 
                 <div class="flex gap-1">
                     <button 
                       @click="page > 1 ? page-- : null" 
                       :disabled="page === 1"
                       class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                     >
                       Anterior
                     </button>
                     
                     <button 
                       v-for="p in visiblePages" 
                       :key="p"
                       @click="page = p"
                       :class="p === page ? 'bg-slate-700 text-white border-slate-700 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-100'"
                       class="px-3 py-1.5 border rounded-md transition-colors font-medium"
                     >
                       {{ p }}
                     </button>

                     <button 
                       @click="page < pageCount ? page++ : null" 
                       :disabled="page === pageCount"
                       class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                     >
                       Próximo
                     </button>
                 </div>
             </div>
          </div>
      </section>
    </div>

    <!-- Snackbar -->
    <v-snackbar
      multi-line
      :color="snack.color"
      v-model="snack.show"
      transition="fade-transition"
    >
      {{ snack.message }}
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  data() {
    return {
      search: "",
      loader: true,
      showSearch: false,
      data: [],
      snack: {
        show: false,
        message: "",
        color: "",
      },
      headers: [
        { text: "Nome/Razão Social", value: "name", class: "text-uppercase caption font-weight-bold" },
        { text: "CPF/CNPJ", value: "document", class: "text-uppercase caption font-weight-bold" },
        { text: "AÇÕES", align: "right", value: "action", sortable: false, class: "text-uppercase caption font-weight-bold" },
      ],
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
    };
  },
  computed: {
    isAdmin() {
      return this.$store.getters.getUserData.group_id === 1;
    },
    isPrefecture() {
      return this.$store.getters.getUserData.table_key === 'prefectures';
    },
    paginationStart() {
      return (this.page - 1) * this.itemsPerPage + 1;
    },
    visiblePages() {
      const pages = [];
      const totalVisible = 5;
      let start = Math.max(1, this.page - Math.floor(totalVisible / 2));
      let end = Math.min(this.pageCount, start + totalVisible - 1);
      if (end - start + 1 < totalVisible) {
        start = Math.max(1, end - totalVisible + 1);
      }
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      
      let url = "/proprietario";
      // Se for prefeitura, o backend já filtra pelo token, mas podemos reforçar aqui
      // ou apenas chamar o endpoint padrão que agora está protegido e filtrado.
      
      this.$http
        .get(url)
        .then((r) => {
          this.data = r.data;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch((e) => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e;
          this.snack.type = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    deleteItem(id) {
      let c = confirm("Deseja excluir o registro?");
      if (c) {
        this.$store.dispatch("setLoader", true);
        this.$http
          .delete(`/proprietario/${id}`)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.initialize();
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    },
  },
};
</script>

<style scoped>
.min-h-screen { min-height: 100vh; }
.bg-slate-50 { background-color: #f8fafc; }
.w-full { width: 100%; }
.p-4 { padding: 1rem; }

.title {
  text-transform: uppercase;
  font-weight: 400;
}

/* Custom Table Override */
.custom-table ::v-deep table { background-color: transparent !important; border-collapse: separate; border-spacing: 0; }
.custom-table ::v-deep thead { background-color: #f8fafc !important; }
.custom-table ::v-deep th { 
  color: #64748b !important; 
  font-weight: 700 !important; 
  text-transform: uppercase !important; 
  font-size: 0.75rem !important; 
  background-color: #f8fafc !important;
  border-bottom: 1px solid #e2e8f0 !important;
  height: 48px !important;
}
.custom-table ::v-deep td { 
  border-bottom: 1px solid #f1f5f9 !important;
  color: #334155 !important;
  font-size: 0.875rem !important;
}
</style>
